import type { BlockConditionAction, BlockConditionOperator } from './types';
import type { ResolvedForm, ResolvedFormConditionRule, PatientFormValues } from './patientFill/types';

export const CONDITION_OPERATOR_TO_WIRE: Record<BlockConditionOperator, string> = {
  'is equal to': 'equals',
  'is not equal to': 'not_equals',
  'is empty': 'empty',
  'is not empty': 'not_empty',
  contains: 'contains',
  'does not contain': 'not_contains',
  'is greater than': 'gt',
  'is less than': 'lt',
};

export const CONDITION_OPERATOR_FROM_WIRE: Record<string, BlockConditionOperator> = {
  equals: 'is equal to',
  not_equals: 'is not equal to',
  empty: 'is empty',
  not_empty: 'is not empty',
  contains: 'contains',
  not_contains: 'does not contain',
  gt: 'is greater than',
  lt: 'is less than',
};

/**
 * The server only knows "visible when the condition matches". A "hide when X"
 * rule is stored as "show when NOT X", so hide is limited to ops with an exact
 * inverse (`gt`/`lt` have none without `gte`/`lte`).
 */
const INVERSE_WIRE_OP: Record<string, string> = {
  equals: 'not_equals',
  not_equals: 'equals',
  empty: 'not_empty',
  not_empty: 'empty',
  contains: 'not_contains',
  not_contains: 'contains',
};

export function invertWireOperator(op: string): string | undefined {
  return INVERSE_WIRE_OP[op];
}

export function canHideWithOperator(operator: BlockConditionOperator): boolean {
  return invertWireOperator(CONDITION_OPERATOR_TO_WIRE[operator]) !== undefined;
}

export function isConditionAction(value: unknown): value is BlockConditionAction {
  return value === 'show' || value === 'hide' || value === 'require';
}

function toComparable(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim().toLowerCase();
  return '';
}

function isBlank(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).every(isBlank);
  return false;
}

export function evaluateConditionRule(rule: ResolvedFormConditionRule, actual: unknown): boolean {
  const expected = toComparable(rule.value);
  const list = Array.isArray(actual) ? actual.map(toComparable) : null;
  const single = toComparable(actual);

  const op = rule.op || rule.operator;
  switch (op) {
    case 'equals':
      return list ? list.includes(expected) : single === expected;
    case 'not_equals':
      return list ? !list.includes(expected) : single !== expected;
    case 'empty':
      return isBlank(actual);
    case 'not_empty':
      return !isBlank(actual);
    case 'contains':
      return list ? list.includes(expected) : single.includes(expected);
    case 'not_contains':
      return list ? !list.includes(expected) : !single.includes(expected);
    case 'gt':
    case 'lt': {
      const a = Number(actual);
      const b = Number(rule.value);
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      return op === 'gt' ? a > b : a < b;
    }
    default:
      return true;
  }
}

/**
 * Re-evaluates block visibility in the browser so follow-up questions appear as
 * the patient answers, instead of after the autosave round-trip. Only blocks the
 * server links to a condition (`condition_id`) are touched; everything else keeps
 * the server's `is_visible`.
 */
export function applyClientConditions(form: ResolvedForm, values: PatientFormValues): ResolvedForm {
  const byId = new Map(form.conditions.map((c) => [c.id, c]));

  const blocks = form.blocks.map((block) => {
    const condition = block.condition_id ? byId.get(block.condition_id) : undefined;
    if (!condition || condition.rules.length === 0) return block;
    const results = condition.rules.map((rule) => evaluateConditionRule(rule, values[rule.field || rule.block_id || '']));
    const visible = condition.operator === 'OR' ? results.some(Boolean) : results.every(Boolean);
    return visible === block.is_visible ? block : { ...block, is_visible: visible };
  });

  return blocks.some((block, i) => block !== form.blocks[i]) ? { ...form, blocks } : form;
}
